import React, { useMemo, useState, useEffect } from "react";
import theme from "../../../styles/theme";

/* ============================================================================
   ACTIVITY SEQUENCING PANEL (D2)
   ----------------------------------------------------------------------------
   OBJECTIF
   - lire uniquement les informations utiles au séquencement
   - permettre l'édition légère de la séquence du package sélectionné
   - rester sobre, premium et cohérent avec le module Activity
============================================================================ */

const T = {
  text: theme?.colors?.text?.primary || "#164D63",
  soft: theme?.colors?.text?.small || "#5C7A89",
  verySoft: theme?.colors?.text?.verysmall || "#7D97A1",
  border: theme?.colors?.borders?.default || "#d6dee4",

  blueText: theme?.Generic?.blue?.pastel?.text || "#1E5066",
  greenText: theme?.Generic?.green?.pastel?.text || "#166A58",
  yellowText: theme?.Generic?.yellow?.pastel?.text || "#8A4A13",
  orangeText: theme?.Generic?.orange?.pastel?.text || "#9A4315",
  greyText: theme?.Generic?.grey?.pastel?.text || "#3F5D6E",
  redText: theme?.Generic?.red?.pastel?.text || "#6E1F1F",
};

/* ============================================================================
   MOCK PACKAGE
============================================================================ */

const MOCK_PACKAGE = {
  id: "p5",
  name: "Poste électrique principal",
  type: "Système",
  logic: "Par système",
  status: "Confirmé",
  stage: "Équipements",
  discipline: "Électricité",
  zone: "Bloc utilités",
  itemsCount: 3,
  effortMix: { materials: 0, labor: 1, equipment: 2 },
  dependencyIds: ["p2", "p3"],
  downstreamIds: ["p6"],
  executionMode: "Parallèle",
  criticality: "Critique",
  sequencingStatus: "En cours",
};

/* ============================================================================
   HELPERS
============================================================================ */

const getSignalColor = (tone) => {
  switch (tone) {
    case "green":
      return T.greenText;
    case "yellow":
      return T.yellowText;
    case "orange":
      return T.orangeText;
    case "red":
      return T.redText;
    default:
      return T.greyText;
  }
};

const getCriticalityTone = (criticality) => {
  switch (criticality) {
    case "Critique":
      return "red";
    case "Forte":
      return "orange";
    case "Modérée":
      return "green";
    default:
      return "grey";
  }
};

const getFlowRole = (data) => {
  const upstream = data?.dependencyIds?.length || 0;
  const downstream = data?.downstreamIds?.length || 0;

  if (upstream === 0) return "Point de départ";
  if (downstream === 0) return "Point de sortie";
  if (upstream >= 2 || downstream >= 2) return "Nœud structurant";
  return "Maillon intermédiaire";
};

const getSequencingReadiness = (data) => {
  const upstream = data?.dependencyIds?.length || 0;
  const criticality = data?.criticality || "";

  if (!data?.stage) {
    return { label: "Non défini", tone: "grey" };
  }

  if (criticality === "Critique" && upstream >= 2) {
    return { label: "Sous tension", tone: "orange" };
  }

  if (upstream === 0) {
    return { label: "Prêt au lancement", tone: "green" };
  }

  return { label: "À coordonner", tone: "yellow" };
};

const getDominantNature = (effortMix = {}) => {
  const entries = [
    { key: "Matériaux", value: effortMix.materials || 0 },
    { key: "Labor", value: effortMix.labor || 0 },
    { key: "Équipements", value: effortMix.equipment || 0 },
  ];

  entries.sort((a, b) => b.value - a.value);
  return entries[0]?.value > 0 ? entries[0].key : "-";
};

const getSupplySignal = (data) => {
  if (data?.criticality === "Critique" && data?.effortMix?.equipment >= 2) {
    return "Appro sensible";
  }

  if (data?.effortMix?.materials >= 2) {
    return "Appro à coordonner";
  }

  return "Signal modéré";
};

/* ============================================================================
   UI BLOCKS
============================================================================ */

const PanelBlock = ({ title, children }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      gap: 10,
      paddingBottom: 2,
    }}
  >
    <div
      style={{
        fontFamily: theme?.fonts?.body || "inherit",
        fontSize: "0.84rem",
        lineHeight: 1.12,
        color: theme?.colors?.text?.caneva || "#c24c2c",
        fontWeight: 600,
      }}
    >
      {title}
    </div>

    {children}
  </div>
);

const KeyValueRow = ({ label, value, valueColor = T.text }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "1fr auto",
      alignItems: "center",
      gap: 12,
    }}
  >
    <div
      style={{
        fontFamily: theme?.fonts?.body || "inherit",
        fontSize: "0.74rem",
        lineHeight: 1.2,
        color: T.soft,
        minWidth: 0,
      }}
    >
      {label}
    </div>

    <div
      style={{
        fontFamily: theme?.fonts?.body || "inherit",
        fontSize: "0.76rem",
        lineHeight: 1.2,
        color: valueColor,
        fontWeight: 600,
        textAlign: "right",
        whiteSpace: "nowrap",
      }}
    >
      {value}
    </div>
  </div>
);

const FieldLabel = ({ children }) => (
  <div
    style={{
      fontFamily: theme?.fonts?.body || "inherit",
      fontSize: "0.72rem",
      lineHeight: 1.1,
      color: T.soft,
      fontWeight: 500,
    }}
  >
    {children}
  </div>
);

const CompactSelect = ({ value, onChange, options = [] }) => (
  <div
    style={{
      position: "relative",
      width: "100%",
      height: 28,
      borderRadius: 3,
      border: `1px solid ${T.border}`,
      background: "#ffffff",
      boxSizing: "border-box",
      overflow: "hidden",
    }}
  >
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        width: "100%",
        height: "100%",
        border: "none",
        background: "transparent",
        color: T.text,
        fontFamily: theme?.fonts?.body || "inherit",
        fontSize: "0.72rem",
        fontWeight: 500,
        padding: "0 24px 0 8px",
        outline: "none",
        appearance: "none",
        WebkitAppearance: "none",
        MozAppearance: "none",
      }}
    >
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>

    <div
      style={{
        position: "absolute",
        right: 8,
        top: "50%",
        transform: "translateY(-50%)",
        pointerEvents: "none",
        fontSize: "0.68rem",
        color: T.soft,
        fontWeight: 700,
      }}
    >
      ▾
    </div>
  </div>
);

const CompactInput = ({ value, onChange, placeholder = "" }) => (
  <input
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    style={{
      width: "100%",
      height: 28,
      borderRadius: 3,
      border: `1px solid ${T.border}`,
      background: "#ffffff",
      boxSizing: "border-box",
      color: T.text,
      fontFamily: theme?.fonts?.body || "inherit",
      fontSize: "0.72rem",
      fontWeight: 500,
      padding: "0 8px",
      outline: "none",
    }}
  />
);

const EmptyState = () => (
  <div
    style={{
      width: "100%",
      display: "flex",
      flexDirection: "column",
      gap: 22,
      boxSizing: "border-box",
    }}
  >
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        minHeight: 12,
      }}
    >
      <div
        style={{
          fontFamily: theme?.fonts?.body || "inherit",
          fontSize: "1.02rem",
          lineHeight: 1.1,
          color: T.text,
          fontWeight: 600,
        }}
      >
        Sequencing Overview
      </div>
    </div>

    <PanelBlock title="Selected package">
      <div
        style={{
          fontFamily: theme?.fonts?.body || "inherit",
          fontSize: "0.78rem",
          lineHeight: 1.35,
          color: T.verySoft,
        }}
      >
        Sélectionner un package dans D1 pour afficher les informations utiles au
        séquencement.
      </div>
    </PanelBlock>
  </div>
);

/* ============================================================================
   METRICS
============================================================================ */

const computeMetrics = (data) => {
  const upstreamCount = data?.dependencyIds?.length || 0;
  const downstreamCount = data?.downstreamIds?.length || 0;
  const totalInterfaces = upstreamCount + downstreamCount;

  return {
    upstreamCount,
    downstreamCount,
    totalInterfaces,
    flowRole: getFlowRole(data),
    readiness: getSequencingReadiness(data),
    dominantNature: getDominantNature(data?.effortMix),
    supplySignal: getSupplySignal(data),
  };
};

/* ============================================================================
   MAIN COMPONENT
============================================================================ */

const ActivitySequencingPanel = ({
  selectedPackage = null,
  useMock = true,
}) => {
  const data = selectedPackage || (useMock ? MOCK_PACKAGE : null);

  const [sequenceStage, setSequenceStage] = useState("Non défini");
  const [sequenceRole, setSequenceRole] = useState("Intermédiaire");
  const [executionMode, setExecutionMode] = useState("Non défini");
  const [sequencingStatus, setSequencingStatus] = useState("Non défini");
  const [upstreamPackages, setUpstreamPackages] = useState("");

  useEffect(() => {
    if (!data) return;

    setSequenceStage(data.stage || "Non défini");
    setExecutionMode(data.executionMode || "Non défini");
    setSequencingStatus(data.sequencingStatus || "Non défini");
    setUpstreamPackages((data.dependencyIds || []).join(", "));

    const upstream = data?.dependencyIds?.length || 0;
    const downstream = data?.downstreamIds?.length || 0;

    if (upstream === 0) {
      setSequenceRole("Départ");
    } else if (downstream === 0) {
      setSequenceRole("Terminal");
    } else {
      setSequenceRole("Intermédiaire");
    }
  }, [data]);

  const metrics = useMemo(() => {
    if (!data) return null;
    return computeMetrics(data);
  }, [data]);

  if (!data || !metrics) return <EmptyState />;

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 22,
        boxSizing: "border-box",
      }}
    >
      {/* EN-TÊTE */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          minHeight: 12,
        }}
      >
        <div
          style={{
            fontFamily: theme?.fonts?.body || "inherit",
            fontSize: "1.02rem",
            lineHeight: 1.1,
            color: T.text,
            fontWeight: 600,
          }}
        >
          Sequencing Overview
        </div>
      </div>

      {/* 1 — IDENTITÉ */}
      <PanelBlock title="Package snapshot">
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <KeyValueRow label="Package" value={data.name} valueColor={T.blueText} />
          <KeyValueRow label="Type" value={data.type || "-"} />
          <KeyValueRow label="Zone" value={data.zone || "-"} />
        </div>
      </PanelBlock>

      {/* 2 — POSITION DANS LA SÉQUENCE */}
      <PanelBlock title="Sequencing position">
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <KeyValueRow
            label="Role in sequence"
            value={metrics.flowRole}
            valueColor={T.blueText}
          />
          <KeyValueRow
            label="Sequence stage"
            value={data.stage || "Non défini"}
            valueColor={data.stage ? T.text : T.verySoft}
          />
          <KeyValueRow
            label="Readiness"
            value={metrics.readiness.label}
            valueColor={getSignalColor(metrics.readiness.tone)}
          />
          <KeyValueRow
            label="Execution mode"
            value={data.executionMode || "Non défini"}
            valueColor={data.executionMode ? T.text : T.verySoft}
          />
        </div>
      </PanelBlock>

      {/* 3 — DÉPENDANCES */}
      <PanelBlock title="Dependencies">
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <KeyValueRow label="Upstream packages" value={`${metrics.upstreamCount}`} />
          <KeyValueRow label="Downstream packages" value={`${metrics.downstreamCount}`} />
          <KeyValueRow
            label="Total interfaces"
            value={`${metrics.totalInterfaces}`}
            valueColor={metrics.totalInterfaces > 1 ? T.orangeText : T.text}
          />
        </div>
      </PanelBlock>

      {/* 4 — SIGNAUX OPÉRATIONNELS */}
      <PanelBlock title="Operational signals">
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <KeyValueRow
            label="Dominant discipline"
            value={data.discipline || "-"}
            valueColor={T.blueText}
          />
          <KeyValueRow
            label="Dominant nature"
            value={metrics.dominantNature}
          />
          <KeyValueRow
            label="Criticality"
            value={data.criticality || "-"}
            valueColor={getSignalColor(getCriticalityTone(data.criticality))}
          />
          <KeyValueRow
            label="Supply signal"
            value={metrics.supplySignal}
            valueColor={T.orangeText}
          />
        </div>
      </PanelBlock>

      {/* 5 — ÉDITION DE LA SÉQUENCE */}
      <PanelBlock title="Sequencing definition">
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <FieldLabel>Sequence stage</FieldLabel>
            <CompactSelect
              value={sequenceStage}
              onChange={setSequenceStage}
              options={[
                "Non défini",
                "Préparation",
                "Infrastructure",
                "Équipements",
                "Finalisation",
              ]}
            />
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <FieldLabel>Role in sequence</FieldLabel>
            <CompactSelect
              value={sequenceRole}
              onChange={setSequenceRole}
              options={[
                "Départ",
                "Intermédiaire",
                "Terminal",
              ]}
            />
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <FieldLabel>Execution mode</FieldLabel>
            <CompactSelect
              value={executionMode}
              onChange={setExecutionMode}
              options={[
                "Non défini",
                "Séquentiel",
                "Parallèle",
                "Mixte",
              ]}
            />
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <FieldLabel>Upstream dependencies</FieldLabel>
            <CompactInput
              value={upstreamPackages}
              onChange={setUpstreamPackages}
              placeholder="Ex. p2, p3"
            />
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <FieldLabel>Sequencing status</FieldLabel>
            <CompactSelect
              value={sequencingStatus}
              onChange={setSequencingStatus}
              options={[
                "Non défini",
                "En cours",
                "À valider",
                "Validé",
                "Critique",
              ]}
            />
          </div>
        </div>
      </PanelBlock>
    </div>
  );
};

export default ActivitySequencingPanel;